import { Link, useRouterState } from '@tanstack/react-router';
import { Bell, Check, ChevronDown, ExternalLink, MoveRight, Trash2 } from 'lucide-react';
import React, { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { VisuallyHidden } from 'react-aria';

import { ButtonClose } from '~/components/buttons/button-close.tsx';
import { CommandBadge } from '~/components/main-navbar/navigation-command-palette/command-badge.tsx';
import { Button } from '~/components/shadcn/button.tsx';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/shadcn/collapsible.tsx';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '~/components/shadcn/dialog/motion-dialog.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/shadcn/tabs.tsx';
import { VirtualizedScrollArea } from '~/components/shadcn/virtualized-scroll-area.tsx';
import type { CleanNotificationWithRelations } from '~/database/supabase/notifications/types.ts';
import { useTranslations } from '~/i18n/use-translations.ts';
import { useSupabase } from '~/integrations/supabase/provider.tsx';
import { getMostPopularTicker, getNotificationDate } from '~/modules/notification/utils.ts';
import { cn } from '~/utils/cn.ts';
import { formatNumber } from '~/utils/format.ts';
import { useOpenChangeHandlers } from '~/utils/hooks/useOpenChangeHandlers.ts';
import { useUserLocale } from '~/utils/theme-utils.ts';

import { updateNotificationViewedStatusClient } from './update-notification-viewed-status.ts';

function getFilteredNotifications(notifications: CleanNotificationWithRelations[], filter: 'all' | 'unread') {
	switch (filter) {
		case 'all':
			return notifications;
		case 'unread':
			return notifications.filter((notification) => !notification.viewed);
	}
}

export interface NotificationsDialogProps {
	readonly className?: string;
	readonly command?: string[];
	readonly count: number;
	readonly isDialogOpen: boolean;
	readonly isMobile?: boolean;
	readonly isOpen: boolean;
	readonly notifications: CleanNotificationWithRelations[];
	readonly setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	readonly setNotifications: React.Dispatch<React.SetStateAction<CleanNotificationWithRelations[]>>;
	readonly showKeyboardNavigation?: boolean;
	readonly textWidth: string;
}

export function NotificationsDialog({
	className,
	command,
	count,
	isDialogOpen,
	isMobile,
	isOpen,
	notifications,
	setIsOpen,
	setNotifications,
	showKeyboardNavigation,
	textWidth,
	...props
}: NotificationsDialogProps) {
	const t = useTranslations();
	const userLocale = useUserLocale();
	const locationHref = useRouterState({
		select: (state) => state.location.href,
	});
	const previousHrefRef = useRef(locationHref);
	const { handleImmediateOpenChange, handleDelayedOpenChange, showTriggerText } = useOpenChangeHandlers({
		isOpen,
		setIsOpen,
	});

	useEffect(() => {
		if (previousHrefRef.current !== locationHref && isOpen) {
			setTimeout(() => setIsOpen(false), 300);
		}

		previousHrefRef.current = locationHref;
	}, [isOpen, locationHref, setIsOpen]);

	function handleCloseFocus(e: Event) {
		e.preventDefault();
	}

	const hasNotifications = count > 0;
	const notificationLabel = hasNotifications
		? t('nav.unreadNotifications', { count: formatNumber(count, userLocale) })
		: t('nav.notifications');

	return (
		<Dialog onOpenChange={handleImmediateOpenChange} open={isOpen}>
			<DialogTrigger
				aria-label={notificationLabel}
				className={cn(
					'group/notifications relative transition-colors duration-500',
					!isMobile && 'border',
					className,
					isOpen
						? isMobile
							? 'before:border-primary/60 before:bg-accent'
							: 'border-border bg-muted px-2 text-primary'
						: !isMobile && 'border-secondary',
				)}
				title={notificationLabel}
				{...props}
			>
				<span className="relative z-10 flex size-4 shrink-0 items-center justify-center">
					<Bell className={cn('size-4', hasNotifications ? 'text-primary' : 'text-foreground')} />
					{hasNotifications && !isDialogOpen && !showKeyboardNavigation ? (
						<span
							aria-hidden="true"
							className="absolute -top-1 -right-1 size-2 rounded-full bg-primary ring-2 ring-card"
						/>
					) : null}
				</span>
				{showTriggerText && !isMobile && !isDialogOpen ? (
					<span
						className={cn(
							'text-left opacity-0',
							textWidth,
							'group-focus-within:opacity-100 group-hover:opacity-100',
							hasNotifications ? 'text-primary' : 'text-foreground',
						)}
					>
						<span className="text-sm">{t('nav.notifications')}</span>
					</span>
				) : null}
				{showKeyboardNavigation ? <CommandBadge command={command} /> : null}
			</DialogTrigger>
			<DialogContent
				classNames={{
					content: cn(
						'z-[calc(var(--z-index-navbar)-1)]',
						'rounded-none rounded-b-none border-border border-x border-y-0 bg-card pt-[1px]',
						'gap-0 p-0 shadow-none sm:rounded-none sm:rounded-b-none sm:shadow-none',
					),
					overlay: 'z-[calc(var(--z-index-navbar)-1)] backdrop-blur-none duration-500',
					portal:
						'z-[calc(var(--z-index-navbar)-1)] absolute left-0 justify-start pt-0 align-start duration-500 sm:left-sidebar-width sm:pt-0',
				}}
				isCloseable={false}
				onCloseAutoFocus={handleCloseFocus}
				onEscapeKeyDown={handleDelayedOpenChange}
				onPointerDownOutside={handleDelayedOpenChange}
				open={isOpen}
				transition={isMobile ? 'fromBottom' : 'fromLeft'}
			>
				<VisuallyHidden>
					<DialogTitle>{t('nav.notifications')}</DialogTitle>
				</VisuallyHidden>
				<Tabs className="flex h-full w-full flex-col gap-0" defaultValue="all">
					<TabsList className="flex h-16 w-full flex-row rounded-none border-border border-b bg-card px-3 py-0">
						<TabsTrigger value="all">{t('notifications.all')}</TabsTrigger>
						<TabsTrigger className="ml-2" value="unread">
							{t('notifications.unread')}
						</TabsTrigger>
						<ButtonClose className="ml-auto" onClose={handleDelayedOpenChange} />
					</TabsList>
					<TabsContent asChild value="all">
						<NotificationTabContent
							isDialogOpen={isDialogOpen}
							notifications={getFilteredNotifications(notifications, 'all')}
							setNotifications={setNotifications}
							userLocale={userLocale}
						/>
					</TabsContent>
					<TabsContent asChild value="unread">
						<NotificationTabContent
							isDialogOpen={isDialogOpen}
							notifications={getFilteredNotifications(notifications, 'unread')}
							setNotifications={setNotifications}
							userLocale={userLocale}
						/>
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	);
}

interface NotificationTabContentProps {
	readonly isDialogOpen: boolean;
	readonly notifications: CleanNotificationWithRelations[];
	readonly setNotifications: React.Dispatch<React.SetStateAction<CleanNotificationWithRelations[]>>;
	readonly userLocale: string;
}

function NotificationTabContent({
	isDialogOpen,
	notifications,
	setNotifications,
	userLocale,
}: NotificationTabContentProps) {
	const t = useTranslations();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const focusedListElement = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isDialogOpen && focusedListElement.current) {
			focusedListElement.current.focus();
		}
	}, [isDialogOpen]);

	return (
		<>
			<VirtualizedScrollArea
				classNames={{
					root: 'w-full pl-1 pr-[8px]',
					scrollbar: 'pr-[1px] py-[1px]',
					viewport: 'h-full w-full outline-hidden',
				}}
				orientation="vertical"
				styles={{
					root: {
						height: 'calc(100dvh - 64px - 64px)',
					},
				}}
				type="always"
				virtualizedProps={{
					selectedIndex,
					setSelectedIndex,
				}}
			>
				{notifications.map((notification, index) => (
					<DropdownNotification
						index={index}
						isLast={index === notifications.length - 1}
						key={notification.id}
						notification={notification}
						ref={index === selectedIndex ? focusedListElement : null}
						setNotifications={setNotifications}
						userLocale={userLocale}
					/>
				))}
			</VirtualizedScrollArea>
			<Link
				className={cn(
					'group flex h-16 w-full items-center justify-center border-border border-t text-foreground hover:underline',
				)}
				preload="intent"
				to="/notifications"
			>
				<span className="mr-1 whitespace-nowrap text-base group-hover:underline">{t('notifications.seeAll')}</span>
				<MoveRight className="ml-1 size-4 text-foreground" />
			</Link>
		</>
	);
}

interface DropdownNotificationProps {
	readonly index: number;
	readonly isLast: boolean;
	readonly notification: CleanNotificationWithRelations;
	readonly setNotifications: React.Dispatch<React.SetStateAction<CleanNotificationWithRelations[]>>;
	readonly userLocale: string;
}

const DropdownNotification = React.forwardRef<HTMLDivElement, DropdownNotificationProps>(
	({ index, isLast, notification, setNotifications, userLocale }, forwardedRef) => {
		const t = useTranslations();
		const { accessToken, supabaseClient } = useSupabase();
		const [isOpen, setIsOpen] = React.useState(false);
		const buttonRef = useRef<HTMLButtonElement>(null);
		const { viewed } = notification;

		async function handleNotificationViewed(targetNotification: CleanNotificationWithRelations) {
			if (!targetNotification.viewed) {
				await updateNotificationViewedStatusClient({
					accessToken,
					notification: targetNotification,
					supabaseClient,
				});

				setNotifications((prev) => [
					...prev.slice(0, index),
					{ ...targetNotification, viewed: true },
					...prev.slice(index + 1),
				]);
			}
		}

		function handleViewEvent(e: React.PointerEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) {
			if ((e instanceof KeyboardEvent && e.key === 'Enter') || e instanceof PointerEvent) {
				e.preventDefault();
				setTimeout(() => {
					void handleNotificationViewed(notification);
				}, 300);
			}
		}

		return (
			<Collapsible
				className={cn(
					'group mt-1 mb-0 flex w-full flex-col space-y-1 rounded-md border bg-card p-2 text-foreground outline-hidden',
					viewed ? 'border-border' : 'border-primary',
					isLast ? 'mb-1' : '',
					'hover:border-border focus:bg-accent',
				)}
				key={index}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						setIsOpen((prev) => !prev);
						buttonRef.current?.focus();
					}
				}}
				open={isOpen}
				ref={forwardedRef}
				tabIndex={0}
			>
				<NotificationHeader notification={notification} userLocale={userLocale} />
				<NotificationContent notification={notification} userLocale={userLocale} />
				<CollapsibleTrigger asChild className="ml-auto" onPointerDown={() => setIsOpen((prev) => !prev)}>
					<Button ref={buttonRef} size="sm" variant="ghost">
						<ChevronDown
							className={cn(
								'size-4 text-foreground transition-transform',
								'group-data-[state=closed]:rotate-0 group-data-[state=open]:rotate-180',
							)}
						/>
					</Button>
				</CollapsibleTrigger>
				<CollapsibleContent
					className={cn(
						'ml-[-8px] flex w-[calc(100%+16px)] flex-row border-border px-2 group-focus:border-border',
						'data-[state=open]:border-t data-[state=open]:pt-2',
					)}
				>
					<Button
						className="flex"
						onKeyDown={handleViewEvent}
						onPointerDown={handleViewEvent}
						size="sm"
						title={t('notifications.confirm')}
						variant="ghost"
					>
						<Check className="size-4 text-foreground" />
					</Button>
					<Button className="ml-1 flex" size="sm" title={t('notifications.clear')} variant="ghost">
						<Trash2 className="size-4 text-foreground" />
					</Button>
					<Button asChild className="ml-auto flex" onKeyDown={(e) => e.stopPropagation()} size="sm" variant="ghost">
						<Link className="flex" search={{ id: String(notification.id) }} to="/notifications">
							<ExternalLink className="size-4 text-foreground" />
						</Link>
					</Button>
				</CollapsibleContent>
			</Collapsible>
		);
	},
);

DropdownNotification.displayName = 'DropdownNotification';

interface NotificationHeaderProps {
	readonly notification: CleanNotificationWithRelations;
	readonly userLocale: string;
}

function NotificationHeader({ notification, userLocale }: NotificationHeaderProps) {
	const t = useTranslations();
	const notificationDate = getNotificationDate(notification);
	const mostLikelyTicker = getMostPopularTicker(notification.company?.instrument);
	const formattedDate = `${notificationDate?.toLocaleDateString(userLocale)} ${notificationDate?.toLocaleTimeString(userLocale)}`;

	return (
		<div className="flex flex-row justify-between">
			<span className="flex whitespace-nowrap text-left" title={t('notifications.eventDate')}>
				{formattedDate}
			</span>
			{mostLikelyTicker ? (
				<Link
					className="flex whitespace-nowrap text-left hover:underline focus:underline"
					onKeyDown={(e) => e.stopPropagation()}
					params={{ ticker: mostLikelyTicker }}
					preload="intent"
					to="/instrument/$ticker"
				>
					{mostLikelyTicker}
				</Link>
			) : null}
		</div>
	);
}

function NotificationContent({ notification, userLocale }: NotificationHeaderProps) {
	const t = useTranslations();
	const { viewed } = notification;
	let value: number | '' = 0;

	switch (true) {
		case !!notification.news:
			return (
				<div className="flex">
					<TextCell title={t('notifications.description')} viewed={notification.viewed}>
						{notification.news?.description}
					</TextCell>
				</div>
			);
		case !!notification.insider_transaction: {
			const price = notification.insider_transaction?.price;
			const volume = notification.insider_transaction?.volume
				? parseInt(notification.insider_transaction.volume, 10)
				: '';
			value = price && volume ? parseInt(price, 10) * volume : '';

			return (
				<>
					<div className="flex flex-row justify-between">
						<ValueCell title={t('notifications.name')} viewed={viewed}>
							{notification.insider_transaction?.person_in_leading_position}
						</ValueCell>
						<ValueCell className="whitespace-normal text-right" title={t('notifications.position')} viewed={viewed}>
							{notification.insider_transaction?.position.replace(/\s/g, ' ')}
						</ValueCell>
					</div>
					<div className="flex flex-row justify-between">
						<ValueCell title={t('notifications.action')} viewed={viewed}>
							{notification.insider_transaction?.character}
						</ValueCell>
						<ValueCell title={t('notifications.volume')} viewed={viewed}>
							{volume?.toLocaleString(userLocale, {
								maximumFractionDigits: 0,
								minimumFractionDigits: 0,
							})}
						</ValueCell>
						<ValueCell title={t('notifications.price')} viewed={viewed}>
							{price}
						</ValueCell>
						<ValueCell title={t('notifications.value')} viewed={viewed}>
							{value.toLocaleString(userLocale, {
								maximumFractionDigits: 0,
								minimumFractionDigits: 0,
							})}
						</ValueCell>
					</div>
				</>
			);
		}
		case !!notification.short_register_holder:
			value = parseFloat(notification.short_register_holder?.position_percent ?? '');

			return (
				<div className="flex flex-row justify-between">
					<ValueCell title={t('notifications.shortHolder')} viewed={viewed}>
						{notification.short_register_holder?.holder}
					</ValueCell>
					<ValueCell title={t('notifications.positionSize')} viewed={viewed}>
						{`${value.toLocaleString(userLocale, {
							maximumFractionDigits: 2,
							minimumFractionDigits: 2,
						})}%`}
					</ValueCell>
				</div>
			);
		case !!notification.short_register_aggregated:
			value = parseFloat(notification.short_register_aggregated?.position_percent ?? '');

			return (
				<div className="flex flex-row justify-between">
					<ValueCell title={t('notifications.description')} viewed={viewed}>
						{t('notifications.totalShortPositionHeld')}
					</ValueCell>
					<ValueCell title={t('notifications.totalShortPosition')} viewed={viewed}>
						{`${value.toLocaleString(userLocale, {
							maximumFractionDigits: 2,
							minimumFractionDigits: 2,
						})}%`}
					</ValueCell>
				</div>
			);
		case !!notification.calendar: {
			const date = notification.calendar?.date ? new Date(notification.calendar.date) : null;

			return (
				<div className="flex flex-col flex-wrap justify-between sm:flex-row">
					<ValueCell title={t('notifications.description')} viewed={viewed}>
						{t('notifications.calendarEvent')}
					</ValueCell>
					<ValueCell title={t('notifications.description')} viewed={viewed}>
						{notification.calendar?.description}
						<span className="ml-1 underline">({date?.toLocaleDateString(userLocale)})</span>
					</ValueCell>
				</div>
			);
		}
		default:
			return null;
	}
}

interface TextCellProps {
	readonly title: string;
	readonly viewed: boolean | null;
}

function TextCell({ children, title, viewed: _viewed }: PropsWithChildren<TextCellProps>) {
	return (
		<span className="text-left font-medium" title={title}>
			{children}
		</span>
	);
}

interface ValueCellProps extends TextCellProps {
	readonly className?: string;
}

function ValueCell({ children, className, title, viewed: _viewed }: PropsWithChildren<ValueCellProps>) {
	return (
		<span className={cn('whitespace-nowrap text-left font-medium', className)} title={title}>
			{children}
		</span>
	);
}
